import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useEffect, useState } from 'react';
import P1Regular from 'components/typography/P1Regular';
import { Group, Skeleton } from '@mantine/core';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';
import P3Medium from 'components/typography/P3Medium';
import LinkWithConfirm from 'components/modals/confirm/LinkWithConfirm';
import { EDIT_DEVICE_PAGE_PATH, REDIRECT_PAGE_PATH } from 'routes/paths';

interface ChildDeviceNameTextProps {
  deviceId: string;
  withLink?: boolean;
  confirmOnNavigate?: boolean;
}

/**
 * Displays child device name by device ID.
 */
export default function ChildDeviceNameText({
  deviceId,
  withLink = false,
  confirmOnNavigate = false,
}: ChildDeviceNameTextProps) {
  const { getAction } = useApi();
  const [device, setDevice] = useState<DeviceGetResponse | null>(null);
  const linkTo = `${REDIRECT_PAGE_PATH.original}?to=${encodeURIComponent(EDIT_DEVICE_PAGE_PATH.insert({ deviceId }))}`;

  useEffect(() => {
    const getDevice = getAction('DeviceGet');

    getDevice({
      parameters: { deviceId: String(deviceId) },
    })
      .then(setDevice)
      .catch(panic);
  }, [getAction, deviceId]);

  if (!device) {
    return <Skeleton height={40} width={200} radius="sm" />;
  }

  const content = (
    <Group spacing={4}>
      <DeviceTypeIcon typeId={device.deviceType.deviceTypeId} size="sm" />
      <P3Medium color="gray.6">(#{device.deviceId})</P3Medium>
      <P1Regular color="gray.8" className="truncate" underline={withLink}>
        {device.deviceName}
      </P1Regular>
    </Group>
  );

  if (!withLink) {
    return content;
  }

  return (
    <LinkWithConfirm
      to={linkTo}
      confirmTitle="Odchod z formulára"
      confirmMessage="Po odchode z formulára sa vykonané zmeny neuložia automaticky. Uistite sa, že ste zmeny uložili. Chcete pokračovať?"
      className="no-underline"
      bypassConfirm={!confirmOnNavigate}
    >
      {content}
    </LinkWithConfirm>
  );
}
